import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Skeleton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useAbortController } from '../../contexts/AbortControllerContext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const getProgressColor = (value) => {
    if (value <= 35) {
        // Red (rgb(200,12,12)) -> hsl(0, 89%, 41%)
        return `hsl(0, 89%, 41%)`;
    } else if (value <= 50) {
        // Transition from Red to Yellow (hsl(0, 89%, 41%) to hsl(57, 81%, 53%))
        const hue = 0 + ((value - 35) / (50 - 35)) * (57 - 0); // Hue interpolation
        const saturation = 89 - ((value - 35) / (50 - 35)) * (89 - 81); // Saturation interpolation
        const lightness = 41 + ((value - 35) / (50 - 35)) * (53 - 41); // Lightness interpolation
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    } else if (value < 100) {
        // Transition from Yellow to Lime (hsl(57, 81%, 53%) to hsl(80, 90%, 50%))
        const hue = 57 + ((value - 50) / (99 - 50)) * (80 - 57); // Hue interpolation
        const saturation = 81 + ((value - 50) / (99 - 50)) * (90 - 81); // Saturation interpolation
        const lightness = 53 - ((value - 50) / (99 - 50)) * (53 - 50); // Lightness interpolation
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    } else {
        // Green (rgb(30,245,0)) -> hsl(114, 100%, 48%)
        return `hsl(114, 100%, 48%)`;
    }
};

const LinkStatus = styled(Box)(({ theme }) => ({
    width: '10px',
    height: '10px',
    borderRadius: '5px',
    backgroundColor: 'red'
}))

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
    '& .MuiButtonGroup-grouped':{
        color: 'rgb(255, 255, 255, 1)',
        fontSize: '12px',
        // width: '100%',
        transition: theme.transitions.create('width'),
        border: '0',
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            border: '0',
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
    },
    '& .MuiButtonGroup-firstButton': {
        borderTopLeftRadius: 7,
        borderBottomLeftRadius: 7
    },
    '& .MuiButtonGroup-middleButton': {
        borderTopRightRadius: 7,
        borderBottomRightRadius: 7,
    }
}))

const StyledButton = styled(Button)(({ theme }) => ({
}))

const StyledMenuList = styled(MenuList)(({ theme }) => ({
    backgroundColor: 'rgba(40, 40, 40, 1)',
    maxHeight: 400,
    borderRadius: 3,
    overflow: 'scroll'
}))



export default function DateSelect(props) {
    const {getLinkSummary, linkSummary, setLinkSummaryOpen, setTransferdate, transferdate, transferDates} = props
    const { abortSimilarGroupRequests } = useAbortController()
    
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'))
    const isSm = useMediaQuery(theme.breakpoints.down('md'))
    const isMd = useMediaQuery(theme.breakpoints.down('lg'))
    const isLg = useMediaQuery(theme.breakpoints.down('xl'))
    
    
    const anchorRef = useRef(null)

    const [openDropDown, setOpenDropDown] = useState(false)
    const [linkPercent, setLinkPercent] = useState(null)

    useEffect(() => {
        const { linkedCount, totalCount } = Object.values(linkSummary?.sources ?? []).reduce((acc, summary) => {
            acc.linkedCount += summary?.link ?? 0
            acc.totalCount += summary?.media ?? 0
            return acc
        }, {linkedCount: 0, totalCount: 0})
        const percent = Math.floor((linkedCount/totalCount)*100)
        if(isNaN(percent) || !isFinite(percent)){
            setLinkPercent(0)
        }
        else{
            setLinkPercent(percent)
        }
    }, [linkSummary])


    const toggleDropDown = () => {
        setOpenDropDown(!openDropDown)
    }

    const handleDropDownClose = (event) => {
        setOpenDropDown(false)
    }

    const handleMenuItemClick = (event, index) => {
        abortSimilarGroupRequests(`${transferdate}`)
        setTransferdate(transferDates[index]);
        setLinkPercent(null)
        setOpenDropDown(false);
        getLinkSummary(transferDates[index])
      };

    const handleSummaryOpen = () => {
        setLinkSummaryOpen(true)
    }


    const StyledMenuItem = styled(MenuItem)(({ theme, transferDate }) => ({
        fontSize: '12px', 
        fontFamily: 'Arial, sans-serif',
        color: 'white', // Change text color to white
        fontWeight: transferDate==transferdate ? 'bold' : 'normal', // Change text color to white
        backgroundColor: transferDate==transferdate ? 'grey !important' : undefined, // Change text color to white

        '&:hover': {
            color: 'rgba(177, 236, 175, 0.9)', // Change hover text color
            backgroundColor: 'rgba(60, 60, 60, 1)', // Change hover background color
        },
    }))



    // Responsive styling
    //   If Xs, show only calendar icon and dropdown icon - no date text, no linking indicator
    //   If Sm or larger, show as standard

    return (
        <React.Fragment>
            <StyledButtonGroup
            sx={{
            }}
                ref={anchorRef}
            >
                <StyledButton
                    onClick={handleSummaryOpen}
                    sx={{ padding: isXs? '5px' : undefined}}
                    
                >
                    {
                        isXs?
                        <></>
                        :
                        <Box
                            sx={{
                                paddingRight: '10px',
    
                            }}
                        >
                            {
                                linkPercent === null
                                ? 
                                <Skeleton
                                    sx={{
                                        bgcolor: 'grey'
                                    }}
                                    variant='circular'
                                    height={10}
                                    width={10}
                                />
                                :
                                <LinkStatus sx={{backgroundColor: getProgressColor(linkPercent)}} />
                            }
                        </Box>
                    }
                    
                    {
                    isXs?
                    <CalendarMonthIcon></CalendarMonthIcon>
                    :
                    transferdate
                    }
                </StyledButton>
                <StyledButton
                    onClick={toggleDropDown}
                    sx={{
                        padding: 0,
                    }}
                >
                    <ExpandMoreIcon
                        sx={{
                            fontSize: '15px',
                            transition: 'transform 0.15s ease-in-out',
                            transform: openDropDown ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                    />
                </StyledButton>
                <Popper
                    sx={{ zIndex: 1 }}
                    open={openDropDown}
                    anchorEl={anchorRef.current}
                    modifiers={[
                        {
                            name: 'width',
                            enabled: true,
                            phase: 'beforeWrite',
                            requires: ['computeStyles'],
                            fn: ({ state }) => {
                                // state.styles.popper.width = `${state.rects.reference.width}px`
                                state.styles.popper.width = 'auto'

                            }
                        }
                    ]}
                    transition
                    disablePortal
                >
                    {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                        transformOrigin:
                            placement === 'bottom' ? 'center top' : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleDropDownClose}>
                                <StyledMenuList id="split-button-menu" autoFocusItem>
                                    {transferDates.map((transferDate, index) => (
                                        <StyledMenuItem
                                            key={index}
                                            selected={transferDates[index] === transferdate}
                                            onClick={(event) => handleMenuItemClick(event, index)}
                                            transferDate={transferDate}
                                        >
                                            {transferDate}
                                        </StyledMenuItem>
                                    ))}
                                </StyledMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                    )}
                </Popper>
            </StyledButtonGroup>
        </React.Fragment>
    );
}